import { Steps } from '@crm/ui';
import { Building2, Globe, type LucideIcon, Scissors, Store, Users } from 'lucide-react';
import type { ComponentType } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks';
import { AUTH_ROUTES } from '@/modules/auth/constants';
import { useSession } from '@/modules/auth/hooks';
import type { JourneyStep } from '@/modules/auth/types';
import { SIGNUP_ROUTES } from '../api';
import { AlreadyRegisteredNotice } from '../components/already-registered-notice';
import { CompanyDetailsStep } from '../components/company-details-step';
import { CompanyLeadForm } from '../components/company-lead-form';
import { LandingStep } from '../components/landing-step';
import { ProfessionalsStep } from '../components/professionals-step';
import { ServicesComboStep } from '../components/services-combo-step';

/** Pedaco da URL que representa cada passo guiado: /cadastro/dados, /servicos... */
const STEP_SLUGS: Record<JourneyStep, string> = {
  COMPANY_DATA: 'dados',
  SERVICES: 'servicos',
  PROFESSIONALS: 'profissionais',
  LANDING_PAGE: 'landing',
};

const STEPS: {
  key: JourneyStep;
  label: string;
  title: string;
  hint: string;
  icon: LucideIcon;
}[] = [
  {
    key: 'COMPANY_DATA',
    label: 'Dados',
    title: '',
    hint: '',
    icon: Building2,
  },
  {
    key: 'SERVICES',
    label: 'Serviços',
    title: '',
    hint: '',
    icon: Scissors,
  },
  {
    key: 'PROFESSIONALS',
    label: 'Profissionais',
    title: '',
    hint: '',
    icon: Users,
  },
  {
    key: 'LANDING_PAGE',
    label: 'Landing Page',
    title: 'Landing Page',
    hint: 'Agora vamos escolher as opções da Landing Page. Você poderá divulgar este endereço para seus clientes acharem seus serviços e realizarem agendamento através dos mecanismos de busca como Google, Yahoo, etc.',
    icon: Globe,
  },
];

/**
 * Cadastro do salao em formato de wizard, aberto pelo CTA da landing do
 * SaaS - trilha de passos no mesmo padrao do `Steps` de `@crm/ui` (usado no
 * wizard do WhatsApp), com auto-save a cada passo. Ordem diferente do
 * assistente pos-login: dados > horarios > servicos > landing page. Ao
 * terminar, o resto (profissionais, WhatsApp, campanhas...) fica para o
 * assistente normal, que ja aparece no topo do painel.
 */
export function SignupWizardPage() {
  useDocumentTitle('Crie Sua Conta');

  const navigate = useNavigate();
  const { step: stepParam } = useParams<{ step?: string }>();
  const { user, isLoading } = useSession();
  const [emailTaken, setEmailTaken] = useState(false);
  // A tela final do passo Landing Page e um fechamento, nao um passo comum -
  // o cabecalho (titulo + dica) do passo some nela.
  const [landingConcluido, setLandingConcluido] = useState(false);

  const wizardStepAtual = user?.journeyStep ?? null;

  // A URL sempre reflete o passo de verdade (o que o servidor guarda) - se
  // a pessoa fechar e voltar num endereco desatualizado, ou colar o link
  // de um passo ja concluido, a propria pagina corrige sozinha.
  useEffect(() => {
    if (!wizardStepAtual) return;
    const slugCerto = STEP_SLUGS[wizardStepAtual];
    if (stepParam !== slugCerto) {
      navigate(`${SIGNUP_ROUTES.WIZARD}/${slugCerto}`, { replace: true });
    }
  }, [wizardStepAtual, stepParam, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user || (!stepParam && !user.journeyStep)) {
    if (emailTaken) {
      return (
        <WizardShell plain>
          <StepHeader icon={Store} title="Crie Sua Conta" />
          <AlreadyRegisteredNotice to={AUTH_ROUTES.LOGIN} actionLabel="Ir para o login" />
        </WizardShell>
      );
    }

    return (
      <WizardShell plain>
        <StepHeader icon={Store} title="Crie Sua Conta" />
        <CompanyLeadForm onEmailTaken={() => setEmailTaken(true)} />
      </WizardShell>
    );
  }

  const wizardStep = user.journeyStep;

  // Cadastro ja concluido (ou conta que nunca passou por aqui): nao reabre o
  // wizard, so aponta para o painel normal.
  if (!wizardStep) {
    return (
      <WizardShell>
        <StepHeader icon={Building2} title="Dados" />
        <AlreadyRegisteredNotice to={AUTH_ROUTES.DASHBOARD} actionLabel="Ir para o painel" />
      </WizardShell>
    );
  }

  const currentIndex = Math.max(
    0,
    STEPS.findIndex((step) => step.key === wizardStep),
  );
  const stepAtual = STEPS[currentIndex] as (typeof STEPS)[number];

  function onStepDone() {
    if (wizardStep === 'LANDING_PAGE') {
      navigate(AUTH_ROUTES.DASHBOARD, { replace: true });
    }
    // Os outros passos: o advance ja invalidou /auth/me, o journeyStep novo
    // vem no proximo render e o passo seguinte abre sozinho.
  }

  function renderStepAtual() {
    switch (wizardStep) {
      case 'COMPANY_DATA':
        return <CompanyDetailsStep onDone={onStepDone} />;
      case 'PROFESSIONALS':
        return <ProfessionalsStep onDone={onStepDone} />;
      case 'SERVICES':
        return <ServicesComboStep onDone={onStepDone} />;
      case 'LANDING_PAGE':
        return <LandingStep onDone={onStepDone} onFaseChange={setLandingConcluido} />;
      default:
        return null;
    }
  }

  return (
    <WizardShell>
      <div className="flex justify-center">
        <Steps
          passos={STEPS.map((step) => ({ key: step.key, label: step.label, icon: step.icon }))}
          atual={currentIndex}
          // So o passo atual e navegavel de verdade - quem avanca e o
          // servidor (Company.journeyStep), nao o clique na trilha.
          onSelect={() => {}}
        />
      </div>

      <div className="space-y-4 px-4 py-6 sm:px-6">
        {landingConcluido ? null : (
          <StepHeader icon={stepAtual.icon} title={stepAtual.title} hint={stepAtual.hint} />
        )}
        {renderStepAtual()}
      </div>
    </WizardShell>
  );
}

function WizardShell({
  children,
  plain,
}: {
  children: React.ReactNode;
  /** Sem o titulo "Cadastro do Estabelecimento" - so a tela de captura do
      lead usa isso, ja que o titulo dela mesmo diz "Cadastro do Salao". */
  plain?: boolean;
}) {
  return (
    <div className="min-h-screen bg-background py-10">
      <div className="mx-auto max-w-2xl space-y-6 px-4 sm:[&_input]:h-11 sm:[&_input]:text-base sm:[&_select]:h-11 sm:[&_select]:text-base sm:[&_label]:text-base">
        {plain ? null : <h1 className="text-center text-2xl font-semibold">Crie Sua Conta</h1>}
        {children}
      </div>
    </div>
  );
}

/** Titulo + hint do passo atual, dentro do proprio box de conteudo. */
function StepHeader({
  icon: Icon,
  title,
  hint,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  hint?: string;
}) {
  if (!title) return null;

  return (
    <div className="text-center">
      <div className="flex items-center justify-center gap-2">
        <Icon className="h-5 w-5 text-primary" />
        <h1 className="text-lg font-semibold">{title}</h1>
      </div>
      {hint ? <p className="mt-1 text-sm text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
